import React, { useState, useCallback } from 'react';
import { CreatorApp } from '../CreatorApp';
import { Modal } from './common/Modal';
import { DraftsModal } from './drafts';
import { EntryModeSelector, AiTextEntry, AiPosterEntry, AiMultiEventResult } from './entry';
import { useUser, useTranslations } from 'react-ui';
import { useFormStore } from '../stores/formStore';
import { useAiExtract } from '../hooks/useAiExtract';
import { clearAllPendingFiles, registerPendingFile } from '../services/imageService';
import type { BalloonEventModel } from 'ts-core';
import type { EntryMode, EventFormData } from '../types';

interface QuickCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  geoLocation?: {
    lng: number;
    lat: number;
  };
  editIdentifier?: string;
  editEvent?: BalloonEventModel;
  cloneEvent?: BalloonEventModel;
  /** Server-side draft ID to load for editing */
  draftId?: string;
  onSuccess?: (event: unknown) => void;
  apiEndpoint?: string;
  apiKey?: string;
}

/**
 * Modal wrapper for quick event creation from map right-click.
 * In create mode, shows an AI entry mode selector before the form.
 */
export const QuickCreateModal: React.FC<QuickCreateModalProps> = ({
  isOpen,
  onClose,
  geoLocation,
  editIdentifier,
  editEvent,
  cloneEvent,
  draftId: externalDraftId,
  onSuccess,
  apiEndpoint,
  apiKey,
}) => {
  const [draftsModalOpen, setDraftsModalOpen] = useState(false);
  const [selectedDraftId, setSelectedDraftId] = useState<string | undefined>(undefined);
  const [entryMode, setEntryMode] = useState<EntryMode>('form');
  const [lowConfidence, setLowConfidence] = useState(false);
  const [multiEventTitles, setMultiEventTitles] = useState<string[]>([]);
  const [aiFormData, setAiFormData] = useState<EventFormData | null>(null);
  const [capturedPosterFile, setCapturedPosterFile] = useState<File | null>(null);
  const [firstSavedDraftId, setFirstSavedDraftId] = useState<string | undefined>(undefined);

  const { user } = useUser();
  const { translated } = useTranslations();
  const { isDirty, resetForm, currentStep } = useFormStore();

  const STEP_TITLE_KEYS = [
    'sc_step_basic_info',
    'sc_step_when_where',
    'sc_step_media',
    'sc_step_details',
    'sc_step_review',
  ] as const;

  const isEditMode = Boolean(editIdentifier);
  const isCloneMode = Boolean(cloneEvent);
  const effectiveDraftId = selectedDraftId || externalDraftId;
  const isDraftMode = Boolean(effectiveDraftId) && !isEditMode;
  // AI entry modes only available in pure create mode
  const showAiSelector = !isEditMode && !isCloneMode && !isDraftMode;

  const handleSingleExtracted = useCallback((formData: EventFormData, isLowConfidence: boolean) => {
    let finalFormData = formData;
    if (capturedPosterFile) {
      const blobUrl = URL.createObjectURL(capturedPosterFile);
      registerPendingFile(blobUrl, capturedPosterFile);
      finalFormData = { ...formData, images: [{ url: blobUrl }, ...(formData.images ?? [])] };
      setCapturedPosterFile(null);
    }
    setAiFormData(finalFormData);
    setLowConfidence(isLowConfidence);
    setEntryMode('form');
  }, [capturedPosterFile]);

  const handleMultipleExtracted = useCallback((count: number, titles: string[], firstDraftId: string | undefined) => {
    setFirstSavedDraftId(firstDraftId);
    setMultiEventTitles(titles.slice(0, count));
    setEntryMode('ai-multi-result');
  }, []);

  const {
    isExtracting,
    isSavingDrafts,
    savedDraftCount,
    totalDraftCount,
    extractionError,
    extractFromText,
    extractFromImage,
    clearError,
  } = useAiExtract({ onSingleExtracted: handleSingleExtracted, onMultipleExtracted: handleMultipleExtracted });

  const handleClose = useCallback(() => {
    resetForm();
    clearAllPendingFiles();
    setEntryMode('form');
    setLowConfidence(false);
    setMultiEventTitles([]);
    setAiFormData(null);
    setCapturedPosterFile(null);
    setFirstSavedDraftId(undefined);
    onClose();
  }, [resetForm, onClose]);

  const handleSuccess = (event: unknown) => {
    onSuccess?.(event);
  };

  const handleOpenDrafts = useCallback(() => {
    setDraftsModalOpen(true);
  }, []);

  const handleCloseDrafts = useCallback(() => {
    setDraftsModalOpen(false);
  }, []);

  const handleSelectDraft = useCallback((draftId: string) => {
    setSelectedDraftId(draftId);
    setDraftsModalOpen(false);
  }, []);

  const handleEntryModeChange = useCallback((mode: EntryMode) => {
    clearError();
    setEntryMode(mode);
  }, [clearError]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-2xl w-full h-full"
    >
      <div className="p-3 md:p-6 h-full flex flex-col gap-3">
        {/* Header */}
        <div className="shrink-0">
          <div className="flex items-center gap-3">
            {entryMode === 'form' && (
              <h2 className="text-2xl font-bold text-gray-900">
                {translated(STEP_TITLE_KEYS[currentStep])}
              </h2>
            )}
            {entryMode === 'ai-text' && (
              <h2 className="text-2xl font-bold text-gray-900">
                {translated('sc_entry_ai_text')}
              </h2>
            )}
            {entryMode === 'ai-poster' && (
              <h2 className="text-2xl font-bold text-gray-900">
                {translated('sc_entry_ai_poster')}
              </h2>
            )}
            {user && !isEditMode && entryMode !== 'ai-multi-result' && (
              <button
                onClick={handleOpenDrafts}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-300 rounded-md hover:bg-primary-50 transition-colors"
              >
                {translated('drafts')}
              </button>
            )}
          </div>
          {geoLocation && !selectedDraftId && !isEditMode && entryMode === 'form' && (
            <p className="text-sm text-gray-600 mt-1">📍 Location selected from map</p>
          )}
        </div>

        {/* AI Entry Mode Selector — create mode only */}
        {showAiSelector && entryMode !== 'ai-multi-result' && (
          <div className="shrink-0">
            <EntryModeSelector mode={entryMode} onChange={handleEntryModeChange} />
          </div>
        )}

        {/* Low-confidence banner */}
        {entryMode === 'form' && lowConfidence && (
          <div className="shrink-0 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <span className="shrink-0">⚠️</span>
            <span className="flex-1">{translated('sc_ai_low_confidence')}</span>
            <button
              type="button"
              onClick={() => setLowConfidence(false)}
              className="shrink-0 hover:opacity-70"
              aria-label="Dismiss"
            >✕</button>
          </div>
        )}

        {/* Main content area */}
        <div className="flex flex-1 min-h-0 w-full">
          {entryMode === 'ai-text' && (
            <AiTextEntry
              isExtracting={isExtracting}
              extractionError={extractionError}
              onExtract={extractFromText}
              onClearError={clearError}
              onCancel={handleClose}
            />
          )}

          {entryMode === 'ai-poster' && (
            <AiPosterEntry
              isExtracting={isExtracting}
              extractionError={extractionError}
              onExtract={extractFromImage}
              onClearError={clearError}
              onCancel={handleClose}
              onFileSelected={setCapturedPosterFile}
            />
          )}

          {entryMode === 'ai-multi-result' && (
            <AiMultiEventResult
              isSavingDrafts={isSavingDrafts}
              savedCount={savedDraftCount}
              totalCount={totalDraftCount}
              eventTitles={multiEventTitles}
              onViewDrafts={handleOpenDrafts}
              onCreateManually={() => {
                if (firstSavedDraftId) setSelectedDraftId(firstSavedDraftId);
                setEntryMode('form');
              }}
            />
          )}

          {entryMode === 'form' && (
            <CreatorApp
              mode={isEditMode ? 'edit' : 'create'}
              event={editEvent}
              editIdentifier={editIdentifier}
              cloneEvent={cloneEvent}
              initialData={aiFormData ?? undefined}
              geoLocation={effectiveDraftId || isEditMode || isCloneMode || aiFormData ? undefined : geoLocation}
              draftId={effectiveDraftId}
              apiEndpoint={apiEndpoint}
              apiKey={apiKey}
              skipProviders={true}
              features={{
                naturalLanguageDates: true,
                duplicateDetection: false,
                templates: false,
                recurringEvents: false,
                aiAssist: true,
                imageUpload: isEditMode || isCloneMode || isDraftMode || Boolean(aiFormData),
                richTextEditor: false,
                mapPicker: isEditMode || isCloneMode || isDraftMode,
              }}
              onSuccess={handleSuccess}
              onCancel={handleClose}
            />
          )}
        </div>
      </div>

      {/* Drafts Modal */}
      {!isEditMode && !isDraftMode && (
        <DraftsModal
          isOpen={draftsModalOpen}
          onClose={handleCloseDrafts}
          onSelectDraft={handleSelectDraft}
          hasUnsavedChanges={isDirty}
        />
      )}
    </Modal>
  );
};
